import * as zod from 'zod';

/**
 * Who last wrote `title`. `manual` is a hard stop for every automated
 * titler — a chat the operator renamed keeps that name. `default` (seeded
 * from the first user message) and `generated` (derived by the agent) are
 * both overwritable.
 */
export const AgentSessionTitleSource = zod.enum([
  'default',
  'manual',
  'generated',
]);

export type AgentSessionTitleSource = zod.infer<typeof AgentSessionTitleSource>;

// Validates the FULL agent_sessions row. The repo's parse() hook
// (apps/feathers-bot/services/hooks/parse.ts) internally calls .partial()
// for create/update/patch, so per-field .default() in this schema would
// be dead code — the migration's column-level .defaultTo(...) handles
// missing fields at insert time. Match the pattern in routine.ts /
// globalSpace.ts: strict full-row schema, DB owns defaults.
export const AgentSessionData = zod.object({
  id: zod.string(),
  routineID: zod.string().nullable(),
  title: zod.string().min(1),
  titleSource: AgentSessionTitleSource,
  tokensConsumed: zod.number().int().nonnegative(),
  nextMessageSequence: zod.number().int().positive(),
  deleted: zod.boolean(),
  createdAt: zod.string().datetime(),
  updatedAt: zod.string().datetime(),
});

export type AgentSessionData = zod.infer<typeof AgentSessionData>;

// Caller-writable subset for patch.
//
// Stripped: server-owned counters (tokensConsumed, nextMessageSequence)
// and createdAt — never caller-writable.
//
// Kept: updatedAt. Postgres column defaults only fire on INSERT, not
// UPDATE, so the `timestamp('updatedAt')` hook in the patch chain has to
// bump it in JS. The hook runs *before* parse() and overwrites any
// caller-supplied value, so allowing updatedAt through the schema doesn't
// let a caller forge it — but omitting it from the schema would make
// parse() strip the value the hook just set, silently losing every bump.
// Long-term fix is a BEFORE UPDATE trigger that bumps updatedAt at the
// DB layer (tracked in
// https://app.asana.com/1/346175681088115/project/1214372247398015/task/1214986612008274);
// when that lands the JS hook and this schema field both go away.
//
// Pattern mirrors UpdatableEquipmentData in equipmentHooks.
export const AgentSessionPatchInput = AgentSessionData.omit({
  tokensConsumed: true,
  nextMessageSequence: true,
  createdAt: true,
});

export type AgentSessionPatchInput = zod.infer<typeof AgentSessionPatchInput>;

/**
 * Request headers agent-bot sets on every tool call to the robot API, naming
 * the chat session and the turn within it. The API journals each write under
 * them so the routine editor can undo a whole turn at once. Shared here so
 * neither side depends on the other's file layout.
 */
export const AGENT_SESSION_HEADER = 'x-agent-session-id';
export const AGENT_TURN_HEADER = 'x-agent-turn-id';
